import { BusinessProfileInfo } from "@/types/user";
import { useCallback, useEffect } from "react";
import { create } from "zustand";

interface BusinessState {
  refreshKey: number;
  isBusiness: boolean;
  setIsBusiness: (notif: boolean) => void;
  currentBusiness: BusinessProfileInfo | null;
  setCurrentBusiness: (business: BusinessProfileInfo | null) => void;
}

export const useCurrentBusinessStore = create<BusinessState>((set) => ({
  refreshKey: 0,
  isBusiness: false,
  setIsBusiness: (notif) => set({ isBusiness: notif }),
  currentBusiness: null,
  setCurrentBusiness: (business) => set({ currentBusiness: business }),
}));

const useCurrentBusinessInfo = () => {
  const { currentBusiness, setCurrentBusiness } = useCurrentBusinessStore();

  // custom setter that persists to localStorage
  const updateBusinessInfo = useCallback(
    (business: BusinessProfileInfo | null) => {
      if (business) {
        localStorage.setItem("CURRENT_BUSINESS", JSON.stringify(business));
      } else {
        localStorage.removeItem("CURRENT_BUSINESS");
      }
      setCurrentBusiness(business);
    },
    [setCurrentBusiness],
  );

  useEffect(() => {
    // Only restore from localStorage if currentBusiness is null/undefined
    if (!currentBusiness) {
      const stored = localStorage.getItem("CURRENT_BUSINESS");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed) {
            // Use Zustand's setter directly to avoid the custom setter's localStorage write
            // This prevents a potential loop where updateBusinessInfo writes to localStorage,
            // which might trigger this effect again
            setCurrentBusiness(parsed);
            localStorage.setItem("CURRENT_BUSINESS", JSON.stringify(parsed));
          }
        } catch (error) {
          // Invalid JSON in localStorage, remove it
          localStorage.removeItem("CURRENT_BUSINESS");
        }
      }
    }
    // Only depend on currentBusiness - setCurrentBusiness is stable from Zustand
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusiness]);

  return { currentBusiness, setCurrentBusiness: updateBusinessInfo };
};

export default useCurrentBusinessInfo;
